/**
 * What a rep reads on an answered question card, for each kind of answer the plan holds
 * (docs/2026-10-05-answered-card.md).
 *
 * The record keeps everything it keeps; this decides what is shown. A saved answer is never shown
 * as its stored form: a kind no formatter reads comes back `null`, and the card then says only
 * that an answer was saved. Each context key names its formatter in `ANSWER_FORMATTERS`, so a new
 * key does not compile until someone decides how it reads.
 */
import { z } from "zod";
import {
  availabilitySchema,
  bookingContextSchema,
  type ContextKey,
  calendarModeSchema,
  eventSchema,
  isContextKey,
  schedulingPolicySchema,
} from "../../../skills/leadcue-reply/scripts/lib/context-contracts.ts";
import { appTime, appWeekdayDayMonth } from "./app-dates.ts";

/** One line of an answer: words, or a link shown as a link. */
export type AnsweredLine = { readonly text: string } | { readonly href: string };

export type AnsweredOptions = {
  /**
   * The zone slot times are read in. The saved slots are instants and carry no zone of their own
   * (`app.ts` converts the rep's local times on the way in), so the card is told one.
   */
  readonly timeZone: string;
};

type Formatter = (value: unknown, options: AnsweredOptions) => readonly AnsweredLine[] | null;

const line = (text: string): AnsweredLine => ({ text });
const count = (value: number, unit: string): string => `${value} ${unit}${value === 1 ? "" : "s"}`;

/** Words the rep typed, kept as typed. */
const words: Formatter = (value) =>
  typeof value === "string" && value.trim().length > 0 ? [line(value.trim())] : null;

const scalar = z.union([z.string().trim().min(1), z.number(), z.boolean()]);
const scalarWords = (value: string | number | boolean): string =>
  typeof value === "boolean" ? (value ? "Yes" : "No") : String(value);
/** A field name as a label: `first_call` reads "First call". */
const labelOf = (name: string): string => {
  const spaced = name.replace(/[_-]+/gu, " ").trim();
  return spaced.charAt(0).toUpperCase() + spaced.slice(1);
};

/**
 * Free-form context: prose, a list of plain values, or a flat record of them. Anything nested is
 * refused rather than flattened into something the rep did not write.
 */
const freeForm: Formatter = (value, options) => {
  if (typeof value === "string") return words(value, options);
  const list = z.array(scalar).min(1).safeParse(value);
  if (list.success) return list.data.map((entry) => line(scalarWords(entry)));
  const record = z.record(z.string(), scalar).safeParse(value);
  if (record.success && Object.keys(record.data).length > 0)
    return Object.entries(record.data).map(([name, entry]) =>
      line(`${labelOf(name)}: ${scalarWords(entry)}`),
    );
  return null;
};

const minutes: Formatter = (value) => {
  const parsed = z
    .union([
      z.number(),
      z
        .string()
        .regex(/^\d{1,3}$/u)
        .transform(Number),
    ])
    .pipe(z.number().int().min(1).max(480))
    .safeParse(typeof value === "string" ? value.trim() : value);
  return parsed.success ? [line(count(parsed.data, "minute"))] : null;
};

const WEEK = [
  { day: 1, name: "Monday" },
  { day: 2, name: "Tuesday" },
  { day: 3, name: "Wednesday" },
  { day: 4, name: "Thursday" },
  { day: 5, name: "Friday" },
  { day: 6, name: "Saturday" },
  { day: 0, name: "Sunday" },
] as const;

/** Working days as a person says them: a run as "Monday to Friday", anything else as a list. */
function daysPhrase(days: readonly number[]): string {
  const chosen = WEEK.map((entry, index) => ({ ...entry, index })).filter((entry) =>
    days.includes(entry.day),
  );
  const first = chosen[0];
  const last = chosen.at(-1);
  if (!first || !last) return "";
  if (chosen.length === 1) return first.name;
  if (chosen.length === 7) return "Every day";
  if (chosen.length > 2 && last.index - first.index === chosen.length - 1)
    return `${first.name} to ${last.name}`;
  const names = chosen.map((entry) => entry.name);
  return `${names.slice(0, -1).join(", ")} and ${names.at(-1)}`;
}

const hours: Formatter = (value) => {
  const parsed = schedulingPolicySchema.safeParse(value);
  if (!parsed.success) return null;
  const policy = parsed.data;
  const notice =
    policy.min_notice_hours === 1
      ? "At least 1 hour’s notice"
      : `At least ${policy.min_notice_hours} hours’ notice`;
  return [
    line(
      `${daysPhrase(policy.working_days)}, ${policy.start_time} to ${policy.end_time} (${policy.host_timezone})`,
    ),
    // No notice and no buffer are the absence of a rule, so they are left unsaid.
    ...(policy.min_notice_hours > 0 ? [line(notice)] : []),
    line(`Up to ${count(policy.max_horizon_days, "day")} ahead`),
    ...(policy.buffer_before_minutes > 0
      ? [line(`${count(policy.buffer_before_minutes, "minute")} free before each call`)]
      : []),
    ...(policy.buffer_after_minutes > 0
      ? [line(`${count(policy.buffer_after_minutes, "minute")} free after each call`)]
      : []),
  ];
};

/** A slot in one zone: the day once when it ends the day it starts. */
function slotWords(start: string, end: string, timeZone: string): string {
  const zone = { timeZone };
  const from = `${appWeekdayDayMonth(start, zone)}, ${appTime(start, zone)}`;
  return appWeekdayDayMonth(start, zone) === appWeekdayDayMonth(end, zone)
    ? `${from} to ${appTime(end, zone)}`
    : `${from} to ${appWeekdayDayMonth(end, zone)}, ${appTime(end, zone)}`;
}

/** Slots and the booking page. When it was verified, by whom and in which mode stay in the record. */
const slots: Formatter = (value, options) => {
  const parsed = availabilitySchema.safeParse(value);
  if (!parsed.success) return null;
  const lines: AnsweredLine[] = parsed.data.slots.map((slot) =>
    line(slotWords(slot.start, slot.end, options.timeZone)),
  );
  if (lines.length > 0) lines.push(line(`Times in ${options.timeZone}`));
  if (parsed.data.booking_url) lines.push({ href: parsed.data.booking_url });
  return lines.length > 0 ? lines : null;
};

const bookingPage: Formatter = (value) => {
  const parsed = calendarModeSchema.safeParse(value);
  if (!parsed.success) return null;
  if (parsed.data.booking_url) return [{ href: parsed.data.booking_url }];
  return [line(parsed.data.mode === "connector" ? "Connected calendar" : "Booked by hand")];
};

const booking: Formatter = (value, options) => {
  const parsed = bookingContextSchema.safeParse(value);
  if (!parsed.success) return null;
  const { status, start, end } = parsed.data;
  if (status === null) return [line("Not booked yet")];
  return [
    line(labelOf(status)),
    ...(start && end
      ? [line(slotWords(start, end, options.timeZone)), line(`Times in ${options.timeZone}`)]
      : []),
  ];
};

const eventFacts: Formatter = (value) => {
  const parsed = eventSchema.safeParse(value);
  if (!parsed.success) return null;
  const event = parsed.data;
  const zone = { timeZone: event.timezone };
  return [
    line(event.name),
    line(
      `${appWeekdayDayMonth(event.starts_at, zone)}, ${appTime(event.starts_at, zone)} (${event.timezone})`,
    ),
    line(event.location.kind === "online" ? "Online" : event.location.venue),
    { href: event.registration_link },
  ];
};

/**
 * Keys a card never saves an answer for: the form sends the rep to the campaign page or to
 * Standing answers instead (`AnswerForm` in `web/main.tsx`). Named, so that leaving a key
 * unformatted is a decision somebody made.
 */
export const SET_OUTSIDE_A_CARD = [
  "campaign_goal",
  "next_step_policy",
  "seller_resources",
  "reply_guards",
  "answers_when_asked",
  "follow_up_promises",
] as const satisfies readonly ContextKey[];
const outsideACard: Formatter = () => null;

export const ANSWER_FORMATTERS: Readonly<Record<ContextKey, Formatter>> = {
  campaign_goal: outsideACard,
  next_step_policy: outsideACard,
  seller_resources: outsideACard,
  reply_guards: outsideACard,
  answers_when_asked: outsideACard,
  follow_up_promises: outsideACard,
  recipient_timezone: words,
  rep_timezone: words,
  seller_name: words,
  seller_identity: words,
  seller_offer: freeForm,
  language: words,
  prospect_name: words,
  rep_name: words,
  meeting_duration_minutes: minutes,
  event: eventFacts,
  scheduling_policy: hours,
  availability: slots,
  calendar_mode: bookingPage,
  booking_context: booking,
  approved_facts: freeForm,
  meeting_hours: freeForm,
  tone_preferences: freeForm,
  approved_examples: freeForm,
  feedback: freeForm,
  commitments: freeForm,
  qualification_context: freeForm,
};

/** A Yes or No given on a decision card, whatever key it was asked under. */
const DecisionSchema = z.object({ kind: z.enum(["yes", "no"]) });

/**
 * The lines a saved answer reads as, or `null` when nothing formats it.
 *
 * A missing value is itself an answer for one key: the rep chose not to state a meeting length.
 */
export function answeredLines(
  key: string,
  value: unknown,
  options: AnsweredOptions,
): readonly AnsweredLine[] | null {
  if (value === undefined)
    return key === "meeting_duration_minutes" ? [line("Don’t state a length")] : null;
  const decision = DecisionSchema.safeParse(value);
  if (decision.success) return [line(decision.data.kind === "yes" ? "Yes" : "No")];
  if (!isContextKey(key)) return words(value, options);
  return ANSWER_FORMATTERS[key](value, options);
}
